import { toPng, toBlob } from 'html-to-image';
// html2canvas-pro is dynamically imported inside the iOS branch of the
// capture functions so it only loads on devices that actually need it.
// See loadHtml2Canvas() below.

export type ExportStyle = 'styled' | 'transparent';

interface CaptureOptions {
  /** 'styled' keeps the element background; 'transparent' strips it */
  style?: ExportStyle;
  /** Scale factor for higher resolution (default 3 ≈ 4K for typical dashboard widths) */
  scale?: number;
}

/**
 * Detect any iOS / iPadOS browser.
 *
 * html-to-image's foreignObject SVG path fails to render Chart.js canvas
 * content on Apple's WebKit, producing blank chart areas in exported PNGs.
 * We route these exports through html2canvas-pro which uses a direct
 * canvas2d rendering pipeline that supports canvases.
 *
 * Apple's App Store policy requires every iOS browser to use the system
 * WKWebView, so Chrome on iOS (CriOS), Edge on iOS (EdgiOS), Firefox on
 * iOS (FxiOS), etc. all share the exact same foreignObject bug as Safari.
 * Detect them all by looking for the iOS device tokens, with a fallback
 * for iPadOS 13+ which reports as desktop Mac (distinguished by touch).
 */
function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPhone / iPad / iPod — covers Safari, Chrome iOS, Edge iOS, Firefox iOS, etc.
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  // iPadOS 13+ Safari reports as Macintosh by default. Distinguish via
  // touch support: real Macs report maxTouchPoints === 0 or undefined.
  if (/Macintosh/.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1) {
    return true;
  }
  return false;
}

/** Shared html-to-image options for all capture methods. */
function buildOptions(options: CaptureOptions) {
  const { style = 'styled', scale = 3 } = options;
  return {
    pixelRatio: scale,
    backgroundColor: style === 'transparent' ? 'transparent' : undefined,
    skipFonts: true, // fonts are already loaded in the browser — avoids CORS errors with Google Fonts
    // cacheBust intentionally OFF: it appends ?t=timestamp to image src URLs,
    // which re-triggers CORS for already-loaded images and can taint Chart.js
    // canvases (causing toDataURL to return 'data:,' and produce empty captures)
    cacheBust: false,
    // Skip the floating export toolbar so it doesn't appear in the capture
    filter: (node: HTMLElement) => {
      return !node?.dataset?.overlayToolbar;
    },
  };
}

/**
 * Build html2canvas options. Mirrors the html-to-image options as closely
 * as possible (same scale, same background handling, same toolbar filter).
 */
function buildHtml2CanvasOptions(options: CaptureOptions) {
  const { style = 'styled', scale = 3 } = options;
  return {
    scale,
    useCORS: true,
    backgroundColor: style === 'transparent' ? null : undefined,
    logging: false,
    // Skip the floating export toolbar
    ignoreElements: (node: Element) => {
      return Boolean((node as HTMLElement)?.dataset?.overlayToolbar);
    },
  };
}

/**
 * Lazy-load html2canvas-pro so it only ships to iOS users.
 *
 * The successful resolution is cached so repeat exports don't re-import,
 * but a rejected import is NOT cached — mobile networks are flaky and
 * caching the rejection would break exports for the rest of the session.
 */
let html2canvasPromise: Promise<typeof import('html2canvas-pro').default> | null = null;
function loadHtml2Canvas(): Promise<typeof import('html2canvas-pro').default> {
  if (!html2canvasPromise) {
    const pending = import('html2canvas-pro').then((m) => m.default);
    // Clear the cache on failure so the next call retries the import.
    pending.catch(() => {
      if (html2canvasPromise === pending) {
        html2canvasPromise = null;
      }
    });
    html2canvasPromise = pending;
  }
  return html2canvasPromise;
}

/**
 * Pre-rasterize every <canvas> inside the element by replacing it with a
 * fresh canvas that has the chart pre-drawn into its 2D context via
 * a synchronous canvas-to-canvas drawImage().
 *
 * Why: html-to-image's cloneCanvasElement calls canvas.toDataURL() and on
 * empty/tainted result returns an empty canvas clone (header + container
 * present, no chart content). Chart.js canvases on mobile Safari/iOS can
 * fail that call because WebKit lazily deallocates the canvas backing
 * store between paint cycles. Replacing the canvas with a fresh one whose
 * buffer was JUST written to guarantees html-to-image's cloning succeeds.
 *
 * canvas-to-canvas drawImage is fully synchronous and doesn't require an
 * Image element or decode step, so the replacement is "hot" the moment
 * we insert it into the DOM.
 *
 * The original canvas is detached during the capture and re-inserted in
 * the same position by the teardown function, so Chart.js's reference is
 * preserved and animations/interactions resume normally afterward.
 *
 * Returns a teardown function that restores the original canvases.
 */
function rasterizeCanvases(root: HTMLElement): () => void {
  const canvases = Array.from(root.querySelectorAll<HTMLCanvasElement>('canvas'));
  const restoreFns: Array<() => void> = [];

  for (const canvas of canvases) {
    if (canvas.width === 0 || canvas.height === 0) continue;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) continue;

    const parent = canvas.parentElement;
    if (!parent) continue;

    const replacement = document.createElement('canvas');
    replacement.width = canvas.width;
    replacement.height = canvas.height;
    replacement.style.cssText = canvas.style.cssText;
    if (canvas.className) replacement.className = canvas.className;

    const ctx = replacement.getContext('2d');
    if (!ctx) continue;

    try {
      ctx.drawImage(canvas, 0, 0);
    } catch {
      continue;
    }

    parent.insertBefore(replacement, canvas);
    parent.removeChild(canvas);

    restoreFns.push(() => {
      if (replacement.parentElement === parent) {
        parent.insertBefore(canvas, replacement);
        parent.removeChild(replacement);
      }
    });
  }

  return () => {
    for (const restore of restoreFns) restore();
  };
}

/**
 * Capture a DOM element as a PNG data URL.
 *
 * Routing:
 * - Mobile WebKit (iOS Safari): uses html2canvas-pro because html-to-image's
 *   foreignObject SVG approach fails to render Chart.js canvas content on
 *   WebKit (the chart area comes out blank).
 * - Everywhere else: uses html-to-image, which supports modern CSS features
 *   like oklch() and color-mix() via the browser's native rendering pipeline.
 */
export async function captureElementAsPngDataUrl(
  element: HTMLElement,
  options: CaptureOptions = {}
): Promise<string> {
  if (isIOS()) {
    const html2canvas = await loadHtml2Canvas();
    const canvas = await html2canvas(element, buildHtml2CanvasOptions(options));
    return canvas.toDataURL('image/png');
  }
  const teardown = rasterizeCanvases(element);
  try {
    return await toPng(element, buildOptions(options));
  } finally {
    teardown();
  }
}

/**
 * Capture a DOM element as a Blob.
 */
export async function captureElementAsBlob(
  element: HTMLElement,
  options: CaptureOptions = {}
): Promise<Blob> {
  if (isIOS()) {
    const html2canvas = await loadHtml2Canvas();
    const canvas = await html2canvas(element, buildHtml2CanvasOptions(options));
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/png');
    });
    if (!blob) throw new Error('Failed to create blob from canvas');
    return blob;
  }
  const teardown = rasterizeCanvases(element);
  try {
    const blob = await toBlob(element, buildOptions(options));
    if (!blob) throw new Error('Failed to create blob from element');
    return blob;
  } finally {
    teardown();
  }
}

/**
 * Download a data URL as a PNG file.
 */
export function downloadPng(dataUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Copy an element's image to the clipboard.
 * Returns true on success, false on failure.
 */
export async function copyElementToClipboard(
  element: HTMLElement,
  options: CaptureOptions = {}
): Promise<boolean> {
  try {
    const blob = await captureElementAsBlob(element, options);
    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': blob }),
    ]);
    return true;
  } catch (err) {
    console.error('Failed to copy image to clipboard:', err);
    return false;
  }
}

/**
 * Save element image to device — uses Web Share API on mobile, falls back to download.
 */
export async function saveElementToDevice(
  element: HTMLElement,
  filename: string,
  options: CaptureOptions = {}
): Promise<boolean> {
  try {
    if (navigator.share && navigator.canShare) {
      const blob = await captureElementAsBlob(element, options);
      const file = new File([blob], filename, { type: 'image/png' });

      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: filename });
          return true;
        } catch (shareError) {
          if (shareError instanceof Error && shareError.name !== 'AbortError') {
            console.warn('Web Share API failed, falling back to download:', shareError);
          }
        }
      }
    }

    // Fallback: download
    const dataUrl = await captureElementAsPngDataUrl(element, options);
    downloadPng(dataUrl, filename);
    return true;
  } catch (err) {
    console.error('Failed to save image to device:', err);
    return false;
  }
}

/**
 * Generate a filename for an exported overlay.
 */
export function generateOverlayFilename(
  metricName: string,
  athleteName?: string
): string {
  const parts = ['cgm'];
  if (athleteName) parts.push(athleteName.replace(/[^a-z0-9]/gi, '_').toLowerCase());
  parts.push(metricName.replace(/[^a-z0-9]/gi, '_').toLowerCase());
  parts.push(String(Date.now()));
  return parts.join('_') + '.png';
}
